import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import app from '@/main.js';
import { usePermissionStore } from './permission';
import { useSupportStore } from './support';
import {
    normalizeSubscriptionDetails,
    SUBSCRIPTION_STATUS,
} from '@/utils/subscriptionStatus';
import { isDentalSector, PROGRAM_ELIGIBLE_SECTORS } from '@/utils/sector';
import { clearLocaleCache, setLocale, writeLocaleCache } from '@/i18n';
import { DEFAULT_LOCALE } from '@/i18n/locales';

export const useUserStore = defineStore('user', {
    state: () => ({
        user: null,
        isAuthenticated: false,
        company: null,
        isPremiumModalVisible: false,
        isPermissionDeniedModalVisible: false,
        isComingSoonModalVisible: false,

        fetching: false, // are we currently fetching the user?
        callbackBus: {}, // functions to call once fetch is done
    }),
    actions: {
        _fetchUserAndTriggerCallbacks() {
            /** Force fetch the user from the API, then perform all the callbacks in callbackBus */
            if (!app) return;

            this.fetching = true;

            const vm = this;
            let fetchSucceeded = false;
            const url = app.config.globalProperties.$env.apiRoot + 'user/me';
            const payload = {
                include: {
                    company: {
                        include: {
                            sector: true,
                        },
                    },
                    sites: true,
                },
            };

            httpAPI(url, payload, 'GET', app.config.globalProperties.$router)
                .then((response) => {
                    if ('success' in response && response.success) {
                        const normalizedUser = {
                            ...response.data,
                            effective_locale:
                                response.data?.effective_locale ||
                                DEFAULT_LOCALE,
                            subscription_details: normalizeSubscriptionDetails(
                                response?.data?.subscription_details ?? {}
                            ),
                        };
                        Object.freeze(normalizedUser);

                        vm.user = normalizedUser;
                        vm.company = normalizedUser.company;
                        fetchSucceeded = true;
                        vm.isAuthenticated = true;
                        setLocale(normalizedUser.effective_locale);
                        writeLocaleCache(
                            normalizedUser,
                            normalizedUser.effective_locale
                        );
                    } else {
                        //const alertStore = useAlertStore();
                        //alertStore.pushError("Failed to fetch data for user store: " + response.message)
                    }
                    this.fetching = false;
                })
                .catch(() => {
                    this.fetching = false;
                })
                .finally(() => {
                    this.fetching = false;

                    // Perform list of callback functions
                    for (const callbackId in this.callbackBus) {
                        const callback = this.callbackBus[callbackId];
                        try {
                            callback(this.user, fetchSucceeded);
                        } catch (error) {
                            console.error(
                                'Error in user store callback',
                                error
                            );
                        }
                        delete this.callbackBus[callbackId]; // Remove it from bus
                    }
                });
        },

        fetchUserData(callback = null, force = false) {
            /** Fetch user from API and perform the given callback once completed.
             *  Use force after saves that need fresh /user/me-derived fields, like effective_locale.
             */

            // Add callback to our bus
            if (callback) {
                const callbackIds = Object.keys(this.callbackBus);
                const lastId = callbackIds.length
                    ? callbackIds[callbackIds.length - 1]
                    : 0;
                this.callbackBus[lastId + 1] = callback;
            }

            // Normal callers reuse an in-flight sync; force starts a fresh sync after writes.
            if (force || !this.fetching) {
                this._fetchUserAndTriggerCallbacks();
            }
        },

        sync(callback = null) {
            /** Fetch the user from the API (if necessary), then perform the callback function once completed */
            if (!this.user || (this.user.company_id && !this.company))
                this.fetchUserData(callback);
            else if (callback) {
                callback(this.user);
            }
        },

        refresh() {
            return new Promise((resolve, reject) => {
                this.fetchUserData((user, succeeded) => {
                    if (succeeded) {
                        resolve(user);
                        return;
                    }
                    reject(new Error('Unable to refresh the current user.'));
                }, !this.fetching);
            });
        },

        clear() {
            this.user = null;
            this.company = null;
            this.isAuthenticated = false;
            this.fetching = false;
            this.callbackBus = {};
            clearLocaleCache();
        },

        companyHasPerm(module) {
            const permStore = usePermissionStore();
            return permStore.companyHasPerm(module);
        },

        companyHasPermOneOf(...modules) {
            const permStore = usePermissionStore();
            return permStore.companyHasPermOneOf(...modules);
        },

        hasPerm(module) {
            const permStore = usePermissionStore();
            return permStore.hasPerm(module);
        },

        hasPermOneOf(...modules) {
            const permStore = usePermissionStore();
            return permStore.hasPermOneOf(...modules);
        },

        canUseMifuIntakeActions() {
            return Boolean(
                this.companyHasPerm('mifu-intake:access') &&
                    (this.hasPermOneOf('library:read', 'library:write') ||
                        this.isCompanyAdmin ||
                        this.isCompanyOwner)
            );
        },

        canUsePrograms() {
            return this.companyHasPerm('programs:access');
        },

        hasDentalOrAlphaAccess() {
            return (
                this.hasAlphaAccess || isDentalSector(this.getCompanySectorName)
            );
        },

        sectorAlphaAccess(...eligibleSectorNames) {
            const company =
                useSupportStore().company || this.company || this.user?.company;
            const sectorName =
                company?.sector?.name || this.getCompanySectorName;

            return (
                Boolean(company) &&
                eligibleSectorNames.includes(sectorName) &&
                this.companyHasPerm('alpha:access')
            );
        },

        programEligibleSectorAlphaAccess() {
            return this.sectorAlphaAccess(...PROGRAM_ELIGIBLE_SECTORS);
        },

        togglePremiumModal(val) {
            this.isPremiumModalVisible = val;
        },

        togglePermissionDeniedModal(val) {
            this.isPermissionDeniedModalVisible = val;
        },

        toggleComingSoonModal(val) {
            this.isComingSoonModalVisible = val;
        },
    },

    getters: {
        getFullName(state) {
            return state.user
                ? ['first_name', 'last_name']
                      .map((key) => state.user[key])
                      .join(' ')
                : 'N/A';
        },

        getCompanySectorName(state) {
            return (
                useSupportStore().company?.sector?.name ??
                state.company?.sector?.name ??
                ''
            );
        },

        isCompanyAdmin(state) {
            return (
                state.isCompanyOwner || Boolean(state.user?.is_company_admin)
            );
        },

        isCompanyOwner(state) {
            return (
                state.isAppAdmin ||
                Boolean(
                    state.user?.id && state.user?.id == state.company?.admin_id
                )
            );
        },

        isAppAdmin(state) {
            return Boolean(state.user?.is_app_admin);
        },

        hasAlphaAccess(state) {
            return Boolean(state.user?.hasAlphaAccess);
        },

        // The audit agent is an alpha feature. Other users get the normal Patty chat.
        hasAuditAgentAccess(state) {
            return Boolean(
                state.user?.is_app_admin || state.user?.hasAlphaAccess
            );
        },

        subscriptionDetails(state) {
            return state.user?.subscription_details ?? null;
        },

        subscriptionStatus(state) {
            return state.user?.subscription_details?.status ?? null;
        },

        isSubscriptionActive(state) {
            return Boolean(state.user?.subscription_details?.hasActivePlan);
        },

        isViewOnly(state) {
            return (
                state.user?.subscription_details?.status ===
                SUBSCRIPTION_STATUS.VIEW_ONLY
            );
        },

        preferredLocale(state) {
            return state.user?.preferred_locale || DEFAULT_LOCALE;
        },

        effectiveLocale(state) {
            return state.user?.effective_locale || DEFAULT_LOCALE;
        },
    },
});
